import { Directive } from '@angular/core';
import { DatePicker } from 'primeng/datepicker';

type DatePickerPatchedState = DatePicker & {
  datepickerAmPmPatchApplied?: boolean;
};

const noonHour = 12;

/** Patches the datepicker to handle AM/PM selection and hour changes correctly when working with ranges. 
 * From https://github.com/primefaces/primeng/issues/15655#issuecomment-3718365179
*/
@Directive({
  selector:
    'p-datepicker[datepickerAmPmPatch], p-datePicker[datepickerAmPmPatch], p-date-picker[datepickerAmPmPatch]',
  standalone: false,
})
export class DatepickerAmPmPatchDirective {
  constructor(private readonly _datePicker: DatePicker) {
    this._applyPatch();
  }

  private _applyPatch(): void {
    const component = this._datePicker as DatePickerPatchedState;

    if (component.datepickerAmPmPatchApplied) {
      return;
    }

    component.datepickerAmPmPatchApplied = true;

    component.toggleAMPMIfNotMinDate = function patchedToggleAMPMIfNotMinDate(
      this: DatePicker,
      newPM: boolean
    ): void {
      const comparableDate = resolveComparableDate(this);
      const { minDate } = this;
      const isSameDay =
        !!minDate &&
        !!comparableDate &&
        minDate.toDateString() === comparableDate.toDateString();

      if (isSameDay && minDate.getHours() >= noonHour) {
        this.pm = true;
      } else {
        this.pm = newPM;
      }
    };

    component.onDateSelect = function patchedDateSelect(
      this: DatePicker,
      event: Event,
      dateMeta: any
    ) {
      // v19 version
      if (this.disabled || !dateMeta.selectable) {
        // v20+ version
        // if (this.$disabled() || !dateMeta.selectable) {
        event.preventDefault();
        return;
      }

      if (this.isMultipleSelection() && this.isSelected(dateMeta)) {
        this.value = this.value.filter((date: Date, i: number) => {
          return !this.isDateEquals(date, dateMeta);
        });
        if (this.value.length === 0) {
          this.value = null;
        }
        this.updateModel(this.value);
      } else {
        if (this.shouldSelectDate(dateMeta)) {
          this.selectDate(dateMeta);
        }
      }

      if (this.hideOnDateTimeSelect && (this.isSingleSelection() || (this.isRangeSelection() && !this.showTime && this.value[1]))) {
        setTimeout(() => {
          event.preventDefault();
          this.hideOverlay();

          if (this.mask) {
            this.disableModality();
          }

          this.cd.markForCheck();
        }, 150);
      }

      this.updateInputfield();
      event.preventDefault();
    }
  }
}

function resolveComparableDate(picker: DatePicker): Date | null {
  const { value } = picker;

  if (picker.isRangeSelection() && Array.isArray(value) && value.length > 0) {
    const [start, end] = value;
    return coerceToDate(end ?? start);
  }

  if (picker.isMultipleSelection() && Array.isArray(value) && value.length > 0) {
    const last = value[value.length - 1];
    return coerceToDate(last);
  }

  return coerceToDate(value);
}

function coerceToDate(candidate: unknown): Date | null {
  if (!candidate) {
    return null;
  }

  if (candidate instanceof Date) {
    return candidate;
  }

  if (typeof candidate === 'string' || typeof candidate === 'number') {
    const parsed = new Date(candidate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}